/**
 * Helper functions for manipulating FilterSets immutably.
 * Use these to update URL filters in React components.
 */

import { serializeFilters } from '@/lib/filter-dsl/serializer';
import type { FilterSet, FilterClause } from '@/lib/filter-dsl/types';

/**
 * Removes a filter clause for a given facet.
 *
 * @param filterSet - Current FilterSet
 * @param facetKey - Facet key to remove
 * @returns New FilterSet without the specified facet
 */
export function removeClause(
  filterSet: FilterSet,
  facetKey: string,
): FilterSet {
  return filterSet.filter((c) => c.facet !== facetKey);
}

/**
 * Updates an entire clause for a given facet.
 * If the facet exists, replaces it; otherwise, appends.
 *
 * @param filterSet - Current FilterSet
 * @param clause - New FilterClause data
 * @returns New FilterSet with clause updated
 */
export function updateClause(
  filterSet: FilterSet,
  clause: FilterClause,
): FilterSet {
  const existingIndex = filterSet.findIndex((c) => c.facet === clause.facet);

  if (existingIndex >= 0) {
    // Replace existing clause
    const updated = [...filterSet];
    updated[existingIndex] = clause;
    return updated;
  } else {
    // Append new clause
    return [...filterSet, clause];
  }
}

function compareClauses(a: FilterClause, b: FilterClause): number {
  const facetCompare = a.facet.localeCompare(b.facet);
  if (facetCompare !== 0) {
    return facetCompare;
  }
  const operatorCompare = a.operator.localeCompare(b.operator);
  if (operatorCompare !== 0) {
    return operatorCompare;
  }
  if (a.values.length !== b.values.length) {
    return a.values.length - b.values.length;
  }
  for (let i = 0; i < a.values.length; i++) {
    const valueCompare = a.values[i].localeCompare(b.values[i]);
    if (valueCompare !== 0) {
      return valueCompare;
    }
  }
  return 0;
}

/**
 * Canonicalizes a FilterSet into a stable serialized string for comparisons.
 *
 * @param filterSet - Array of FilterClause objects
 * @returns Canonicalized filter string (sorted by facet/operator/values)
 */
export function canonicalizeFilterSet(filterSet: FilterSet): string {
  if (filterSet.length === 0) {
    return '';
  }

  const normalized = filterSet.map((clause) => ({
    ...clause,
    values: [...clause.values].sort((a, b) => a.localeCompare(b)),
  }));
  const sorted = normalized.sort(compareClauses);
  return serializeFilters(sorted);
}
